import React, { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  CalculatorIcon, BrainIcon, FileTextIcon, ShieldIcon, FileSpreadsheetIcon, PackageIcon,
  LayoutDashboardIcon, FolderIcon, TruckIcon } from 'lucide-react';
import NavItem from './NavItem';
import UserProfile from './UserProfile';
import CompanyNavGroup from './CompanyNavGroup';
import { usePermissions } from '../../hooks/usePermissions';
import { isCatalogEnabled } from '../../lib/featureFlags';
import { useHubEnabled, useHubPages } from '../../hooks/hub/useHub';
import gbLogo from '../../assets/images/gb-logo.png';
import SidebarV2 from './SidebarV2';
import { useV2Preview, useV2Shell } from './useV2Shell';
import ShellToggle from './ShellToggle';

interface SidebarProps {
  isOpen: boolean;
}

/** Today's sidebar, unchanged for v1 users (pinned by Sidebar.v1v2.test.tsx). */
const SidebarV1: React.FC<SidebarProps> = ({ isOpen }) => {
  const location = useLocation();
  const [isHovered, setIsHovered] = useState(false);
  const { isAdmin, canAccessPortal, canAccessFinance, canUseBrandCatalog, canOperateCatalog, canShipToWarehouse } = usePermissions();

  // Hub hooks — called unconditionally per CLAUDE.md rules; enabled flags guard the queries
  const hubEnabled = useHubEnabled();
  const v2Preview = useV2Preview();
  const isHubWorkspace = location.pathname.startsWith('/hub');
  const { data: hubPages } = useHubPages(hubEnabled && isHubWorkspace);

  // Check if we're currently on an admin route
  const isOnAdminRoute = location.pathname.startsWith('/admin');

  // Use admin portal paths when on admin routes, regular portal paths otherwise
  const portalBasePath = isOnAdminRoute ? '/admin/portal' : '/portal';

  // Sidebar is collapsed by default, expands on hover (or when opened on mobile)
  // isOpen is only ever true below lg (hamburger is lg:hidden), so desktop hover is unchanged
  const shouldExpand = isHovered || isOpen;

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <div
      className={`bg-brand-navy text-white fixed inset-y-0 left-0 z-40 transform transition-all duration-300 ease-out ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 ${shouldExpand ? 'w-64 shadow-2xl' : 'w-20'}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="h-16 flex items-center border-b border-gray-700 transition-all duration-300 ease-out">
        <div className="w-full">
          <UserProfile isCollapsed={!shouldExpand} direction="down" />
        </div>
      </div>
      
      <div className="h-[calc(100vh-4rem)] flex flex-col">
        {/* Workspace switcher — only shown when brand has Hub enabled and sidebar is expanded */}
        {hubEnabled && shouldExpand && (
          <div className="px-3 pt-3 pb-1">
            <div className="flex rounded-lg bg-white/10 p-0.5 text-xs font-medium">
              <Link
                to="/portal"
                aria-current={!isHubWorkspace ? 'page' : undefined}
                className={`flex-1 text-center py-1.5 rounded-md transition-colors ${
                  !isHubWorkspace
                    ? 'bg-white text-brand-navy'
                    : 'text-white/70 hover:text-white'
                }`}
              >
                GlobalBridge
              </Link>
            </div>
          </div>
        )}

        <nav className="flex-1 mt-2 px-2">
          {/* Hub workspace nav — replaces regular nav body when in Hub mode */}
          {isHubWorkspace && hubEnabled ? (
            <div className="space-y-0.5">
              {(hubPages ?? []).map((page) => (
                <NavItem
                  key={page.id}
                  icon={<LayoutDashboardIcon size={16} />}
                  label={page.name}
                  href={`/hub/pages/${page.id}`}
                  isCollapsed={!shouldExpand}
                />
              ))}
            </div>
          ) : (
            <>
              <div className="space-y-0.5">
                {canAccessPortal() && (
                  <NavItem
                    icon={<BrainIcon size={16} />}
                    label="Intelligence Suite"
                    href={portalBasePath}
                    isCollapsed={!shouldExpand}
                    hasDropdown={true}
                    dropdownItems={[
                      { label: 'Total View', href: `${portalBasePath}/total?tab=sales` },
                      { label: 'Style View', href: portalBasePath },
                      { label: 'Categories', href: `${portalBasePath}/categories` },
                    ]}
                  />
                )}
                <NavItem icon={<FileTextIcon size={16} />} label="Reports" href="/reports" isCollapsed={!shouldExpand} />
                {/* Shipment notices: the roles that may file one (the API's ACTING_ROLES). */}
                {canShipToWarehouse() && (
                  <NavItem
                    icon={<TruckIcon size={16} />}
                    label="Shipments"
                    href="/shipments"
                    isCollapsed={!shouldExpand}
                    hasDropdown={true}
                    dropdownHeaderLink={true}
                    dropdownItems={[
                      { label: 'Ship to warehouse', href: '/shipments/new' },
                      { label: 'Shipments sent', href: '/shipments' },
                    ]}
                  />
                )}
                {hubEnabled && (
                  <NavItem icon={<FolderIcon size={16} />} label="My Reports" href="/my-reports" isCollapsed={!shouldExpand} />
                )}
                {/* Catalog (product onboarding) — live for GB admins only
                    (Ari, 2026-09-10). The operator picks the brand in the
                    navbar switcher; Tracking is the cross-brand progress
                    table and is reachable only from here. */}
                {isCatalogEnabled() && (canOperateCatalog() || canUseBrandCatalog()) && (
                  <NavItem
                    icon={<PackageIcon size={16} />}
                    label="Catalog"
                    href="/catalog"
                    isCollapsed={!shouldExpand}
                    hasDropdown={true}
                    dropdownHeaderLink={true}
                    dropdownItems={[
                      { label: 'Catalog', href: '/catalog' },
                      { label: 'Marketplaces', href: '/catalog/marketplaces' },
                      { label: 'Product Onboarding', href: '/catalog/connect' },
                    ]}
                  />
                )}
              </div>

              <div className="border-t border-white/30 my-2"></div>

              <div className="space-y-0.5">
                {canAccessFinance() && (
                  <NavItem icon={<FileSpreadsheetIcon size={16} />} label="P&L statement" href="/profit-loss" isCollapsed={!shouldExpand} />
                )}
                {canAccessFinance() && (
                  <NavItem icon={<CalculatorIcon size={16} />} label="Reconcile" href="/reconcile" isCollapsed={!shouldExpand} />
                )}
              </div>

              {/* Admin Section - Only visible to ADMIN users */}
              {isAdmin() && (
                <>
                  <div className="border-t border-white/30 my-2"></div>
                  <div className="space-y-0.5">
                    <NavItem
                      icon={<ShieldIcon size={16} />}
                      label="Admin"
                      href="/admin"
                      isCollapsed={!shouldExpand}
                      hasDropdown={true}
                      dropdownItems={[
                        { label: 'Executive Dashboard', href: '/admin/dashboard' },
                        { label: 'Operations', href: '/admin/operations?tab=warehouses' },
                        { label: 'Brands', href: '/admin/brands' }
                      ]}
                    />
                  </div>
                </>
              )}

              <CompanyNavGroup isCollapsed={!shouldExpand} />
            </>
          )}
        </nav>

        {/* Portal toggle back to the new portal: v2-preview users only (no DOM for anyone else). */}
        {v2Preview && (
          <div className="px-2 pb-2">
            <ShellToggle to="v2" isCollapsed={!shouldExpand} />
          </div>
        )}

        {/* GlobalBridge service signature */}
        <div className={`h-14 flex items-center justify-center border-t border-white/30 transition-all duration-300 ease-out ${shouldExpand ? 'px-6' : 'px-2'}`}>
          <img
            className={`object-contain mix-blend-lighten brightness-200 opacity-70 transition-all duration-300 ease-out ${shouldExpand ? 'h-7' : 'h-6'}`}
            src={gbLogo}
            alt="GlobalBridge"
          />
        </div>
      </div>
    </div>
  );
};

/**
 * The frame's sidebar. v2 shell (the mock's sidebar) only when
 * `isV2PreviewEnabled(user)`; every v1 user gets today's sidebar.
 */
const Sidebar: React.FC<SidebarProps> = (props) => (useV2Shell() ? <SidebarV2 {...props} /> : <SidebarV1 {...props} />);

export default Sidebar;
